import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from './baseUrl'
import { SESSION_EXPIRED_EVENT, isAuthProbe } from '@/lib/session'

const BASE_URL = API_BASE_URL

/**
 * Axios instance configured for Sanctum SPA cookie auth.
 *  - withCredentials: true so the laravel_session cookie is sent
 *  - Reads XSRF-TOKEN cookie and sets X-XSRF-TOKEN header automatically
 *  - Accept: application/json so Laravel renders JSON error envelopes
 *
 * See MIGRATION_CONTRACT.md §3.
 */
export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  withXSRFToken: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

/** Prime the CSRF cookie. Call once before login. */
export async function primeCsrf(): Promise<void> {
  await api.get('/sanctum/csrf-cookie')
}

/**
 * A filter the server could not apply. The API reports these in
 * `meta.filters.unrecognized`; a dropped filter must never be invisible
 * (defect D-002, _specs/40_E2E_TEST_STRATEGY.md §3.2), so we re-broadcast it
 * as a DOM event that AppShell turns into a warning toast.
 */
export interface IgnoredFiltersDetail {
  url: string
  applied: string[]
  unrecognized: string[]
}

export const IGNORED_FILTERS_EVENT = 'bigroi:ignored-filters'

interface ApiEnvelope {
  success: boolean
  data: unknown
  meta?: {
    filters?: { applied?: string[]; unrecognized?: string[] }
    [k: string]: unknown
  }
}

/** Unwrap response envelope ({ success, data, meta }) -> data. */
api.interceptors.response.use(
  (res) => {
    if (res.data && typeof res.data === 'object' && 'success' in res.data) {
      const envelope = res.data as ApiEnvelope
      const ignored = envelope.meta?.filters?.unrecognized ?? []
      if (ignored.length > 0) {
        const detail: IgnoredFiltersDetail = {
          url: res.config?.url ?? '',
          applied: envelope.meta?.filters?.applied ?? [],
          unrecognized: ignored,
        }
        // Loud in the console for developers…
        console.error(
          `[api] ${detail.url}: the server ignored filter(s) ${ignored.join(', ')} — ` +
            'they are not supported by this endpoint. Results are UNFILTERED for those keys.',
          detail,
        )
        // …and visible to the user, via AppShell's listener.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(IGNORED_FILTERS_EVENT, { detail }))
        }
      }
      return { ...res, data: envelope.data }
    }
    return res
  },
  (err: AxiosError<{ success: false; error: { code: string; message: string; details?: unknown; trace_id?: string } }>) => {
    // 419 = CSRF token missing or expired (e.g. after an idle session). Laravel's CSRF
    // check runs BEFORE any controller, so the rejected request had no side effects and
    // is safe to replay: refresh the token once and retry. A second 419 is a real failure.
    const cfg = err.config as (InternalAxiosRequestConfig & { _csrfRetried?: boolean }) | undefined
    if (err.response?.status === 419 && cfg && !cfg._csrfRetried && !cfg.url?.includes('/sanctum/csrf-cookie')) {
      cfg._csrfRetried = true
      return primeCsrf().then(() => api.request(cfg))
    }
    // A 401 on an ordinary request means the session died (idle expiry, revoked from another
    // device, password changed elsewhere). Tell the auth provider once, so the user is taken to
    // sign-in with an explanation instead of seeing every panel fail.
    if (err.response?.status === 401 && typeof window !== 'undefined' && !isAuthProbe(cfg?.url)) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
    }
    const envelope = err.response?.data
    if (envelope && typeof envelope === 'object' && 'error' in envelope) {
      const e = envelope.error
      // QA-M01: the server's database is behind the code. Tell the shell, so EVERY role sees one
      // calm notice instead of panels that each say "the server hit an error".
      if ((e.details as { reason?: string } | undefined)?.reason === 'DATABASE_UPDATE_PENDING' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(DB_UPDATE_PENDING_EVENT))
      }
      const enriched = Object.assign(new Error(e.message), {
        code: e.code,
        details: e.details,
        traceId: e.trace_id,
        status: err.response?.status,
        original: err,
      })
      return Promise.reject(enriched)
    }
    return Promise.reject(err)
  },
)

/** Fired when any API call answers 503 DATABASE_UPDATE_PENDING (pending migrations on the server). */
export const DB_UPDATE_PENDING_EVENT = 'hrms:db-update-pending'

export type ApiError = Error & {
  code: string
  details?: unknown
  traceId?: string
  status?: number
}
